import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { ThemeProvider } from './components/ThemeProvider';
import { Layout } from './components/Layout';
import { Chatbot } from './components/Chatbot';
import { PushNotificationPrompt } from './components/PushNotificationPrompt';
import { Toaster } from './components/ui/sonner';
import { LandingPage } from './pages/LandingPage';
import { ShopperLandingPage } from './pages/ShopperLandingPage';
import { BusinessLandingPage } from './pages/BusinessLandingPage';
import { RegisterPage } from './pages/RegisterPage';
import { LoginPage } from './pages/LoginPage';
import { KuponsPage } from './pages/KuponsPage';
import { KuponDetailPage } from './pages/KuponDetailPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';
import { ProfilePage } from './pages/ProfilePage';
import { BusinessDashboard } from './pages/BusinessDashboard';
import { BusinessKuponsPage } from './pages/BusinessKuponsPage';
import { BusinessCampaignsPage } from './pages/BusinessCampaignsPage';
import { BusinessReviewsPage } from './pages/BusinessReviewsPage';
import { RewardsPage } from './pages/RewardsPage';
import { LegalPage } from './pages/LegalPage';
import BusinessPublicProfile from './pages/BusinessProfilePage';
import { DirectoryPage } from './pages/DirectoryPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboard from './pages/AdminDashboard';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DataProcessingAgreement } from './pages/DataProcessingAgreement';
import { DemoAgentPage } from './pages/DemoAgentPage';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import OfferLandingPage from './pages/OfferLandingPage';
import { initPWAAnalytics, pwaAnalytics } from './utils/pwaAnalytics';

// Initialize PWA Analytics
initPWAAnalytics();

// Page view tracker component
const PageViewTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view on route change
    pwaAnalytics.instance?.trackPageView(location.pathname);
  }, [location]);
  
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRole }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRole && user.role !== allowedRole) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Admin Protected Route
const AdminRoute = ({ children }) => {
  const admin = JSON.parse(localStorage.getItem('adminUser') || 'null');
  if (!admin) {
    return <Navigate to="/nakamadmm-admin" replace />;
  }
  return children;
};

function App() {
  return (
    <ThemeProvider defaultTheme="light">
      <BrowserRouter>
        <PageViewTracker />
        <Layout>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<ShopperLandingPage />} />
            <Route path="/compradores" element={<ShopperLandingPage />} />
            <Route path="/para-negocios" element={<BusinessLandingPage />} />
            <Route path="/landing-hub" element={<LandingPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/rewards" element={<RewardsPage />} />
            <Route path="/privacy" element={<LegalPage />} />
            <Route path="/terms" element={<LegalPage />} />
            <Route path="/cookies" element={<LegalPage />} />
            <Route path="/legal-notice" element={<LegalPage />} />
            <Route path="/legal/:type" element={<LegalPage />} />
            <Route path="/legal/dpa" element={<DataProcessingAgreement />} />
            <Route path="/demo-reactivacion" element={<DemoAgentPage />} />
            <Route path="/oferta/:slug" element={<OfferLandingPage />} />
            <Route path="/negocios" element={<DirectoryPage />} />
            <Route path="/negocio/:id" element={<BusinessPublicProfile />} />
            
            {/* Customer Routes */}
            <Route path="/kupons" element={<KuponsPage />} />
            <Route 
              path="/kupon/:id" 
              element={
                <ProtectedRoute allowedRole="customer">
                  <KuponDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/checkout/success" 
              element={
                <ProtectedRoute allowedRole="customer">
                  <CheckoutSuccessPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute allowedRole="customer">
                  <ProfilePage />
                </ProtectedRoute>
              } 
            />
            
            {/* Business Routes */}
            <Route 
              path="/business/dashboard" 
              element={
                <ProtectedRoute allowedRole="business">
                  <BusinessDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/business/kupons" 
              element={
                <ProtectedRoute allowedRole="business">
                  <BusinessKuponsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/business/campaigns" 
              element={
                <ProtectedRoute allowedRole="business">
                  <BusinessCampaignsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/business/reviews" 
              element={
                <ProtectedRoute allowedRole="business">
                  <BusinessReviewsPage />
                </ProtectedRoute>
              } 
            />

            {/* Admin Routes - Hidden URL */}
            <Route path="/nakamadmm-admin" element={<AdminLoginPage />} />
            <Route 
              path="/nakamadmm-admin/dashboard" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Global Components */}
          <Chatbot />
          <PushNotificationPrompt />
          <PWAInstallPrompt />
          <CookieConsentBanner />
          <Toaster position="top-right" />
        </Layout>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
